import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ModalComponent } from '../../../ui/modal-component/modal-component.component';
import { FormWrapperComponent } from '../../../ui/form-wrapper/form-wrapper.component';
import { SearchInputComponent } from '../../../inputs/search-input/search-input.component';
import { PaginationComponent } from '../../../inputs/pagination/pagination.component';
import { ContactsService } from '../../../../services/contacts/contacts.service';
import { IContact, IPagination } from '../../../../types/contactModule';
import { CampaignsService } from '../../../../services/campaign/campaigns-api.service';
import {
  CampaignDetails,
  EngagementContactList,
} from '../../../../pages/organization/campaigns/types/campaignDetails';
import { CustomizableButtonComponent } from '../../../ui/customizable-button/customizable-button.component';
import { PromptCardComponent } from '../../../toasts-and-cards/prompt-card/prompt-card.component';
import { debounce } from 'lodash';
import { DataTableComponent } from '../../../ui/data-table/data-table.component';
import { StatusComponent } from '../../../ui/status/status.component';

@Component({
  selector: 'app-group-members-list',
  standalone: true,
  imports: [
    MatTooltipModule,
    ModalComponent,
    FormWrapperComponent,
    SearchInputComponent,
    PaginationComponent,
    CustomizableButtonComponent,
    PromptCardComponent,
    DataTableComponent,
    StatusComponent,
  ],
  templateUrl: './group-members-list.component.html',
  styleUrls: ['../campaign-form.css', './group-members-list.component.css'],
})
export class GroupMembersListComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  @Output() onClose = new EventEmitter();
  @Input({ required: true }) openModal = false;
  @Input({ required: true }) title = 'Marketing Team';
  @Input({ required: true }) groupName!: 'organization' | 'tag' | 'segment';
  @Input({ required: true }) groupId!: number | string;
  contactsService = inject(ContactsService);
  campaignsService = inject(CampaignsService);
  campaign!: CampaignDetails;
  blacklistedContacts!: string | null;
  contacts: IContact[] = [];
  contactAddedToBlacklist = false;
  contactRemovedFromBlacklist = false;
  touched = false;
  showBlacklistPrompt = false;
  contactsPage: number = 0;
  totalPages: number = 0;

  // queries
  searchTerm: string = '';
  params: Record<string, any> = {
    size: 10,
    sort: 'fullName,asc',
  };

  debounceSearch!: ReturnType<typeof debounce>;

  constructor() {
    this.debounceSearch = debounce(this.searchContacts.bind(this), 1000);
  }

  updateSearchTerm(term: string) {
    this.searchTerm = term;
    this.contactsPage = 0;
    if (term.length < 1) {
      // Clearing the search lists every member again.
      delete this.params['firstName.contains'];
      this.debounceSearch.cancel();
      this.contactsService.fetchGroupContacts(
        this.groupName,
        this.groupId,
        this.updatedParams
      );
      return;
    } else {
      this.params['firstName.contains'] = term;
      this.debounceSearch();
    }
  }

  searchContacts() {
    this.contactsService.fetchGroupContacts(
      this.groupName,
      this.groupId,
      this.params['firstName.contains'] && this.updatedParams
    );
  }

  get updatedParams() {
    return { ...this.params, page: this.contactsPage };
  }

  /** "Ada Lovelace", for the row and its buttons' names. */
  fullName(contact: IContact): string {
    return `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
  }

  // pagination
  /**
   * Stores the new page size only: the pagination follows a size change with
   * changePage(0), and that fetches the first page at the new size.
   */
  changePageSize(size: number) {
    this.params['size'] = Number(size) || 10;
    this.contactsPage = 0;
  }

  updateContactsPage(newPage: number) {
    this.contactsPage = newPage;
    this.contactsService.fetchGroupContacts(
      this.groupName,
      this.groupId,
      this.updatedParams
    );
  }
  goToNextPage() {
    this.contactsPage += 1;
    // perform some api call
    this.contactsService.fetchGroupContacts(
      this.groupName,
      this.groupId,
      this.updatedParams
    );
  }
  goToPreviousPage() {
    this.contactsPage -= 1;
    // perform some api call
    this.contactsService.fetchGroupContacts(
      this.groupName,
      this.groupId,
      this.updatedParams
    );
  }

  // displayedContacts: IContact[] = [];
  // currentIndex = 0;
  // itemsPerPage = 7;

  // loadMore() {
  //   if (this.currentIndex + this.itemsPerPage < this.contacts.length) {
  //     this.currentIndex += this.itemsPerPage;
  //     this.updateDisplayedContacts();
  //   }
  // }

  // loadPrevious() {
  //   if (this.currentIndex - this.itemsPerPage >= 0) {
  //     this.currentIndex -= this.itemsPerPage;
  //     this.updateDisplayedContacts();
  //   }
  // }

  // updateDisplayedContacts() {
  //   this.displayedContacts = this.contacts.slice(
  //     this.currentIndex,
  //     this.currentIndex + this.itemsPerPage
  //   );
  // }

  // getTotalPages(): number {
  //   return Math.ceil(this.contacts?.length / this.itemsPerPage);
  // }

  addContactToBlacklist(uuid: string) {
    this.touched = true;
    this.contactAddedToBlacklist = true;
    if (this.blacklistedContacts) {
      this.blacklistedContacts += `,${uuid}`;
    } else {
      this.blacklistedContacts = uuid;
    }
  }

  removeContactFromBlacklist(uuid: string) {
    this.touched = true;
    if (this.blacklistedContacts) {
      const blacklistedArray = this.blacklistedContacts.split(',');
      const index = blacklistedArray.indexOf(uuid);
      if (index > -1) {
        blacklistedArray.splice(index, 1);
        this.blacklistedContacts = blacklistedArray.join(',');
      }
    }
  }

  saveChanges() {
    if (!this.touched) {
      this.onClose.emit();
      return;
    }

    if (this.contactAddedToBlacklist && this.blacklistedContacts !== '') {
      this.showBlacklistPrompt = true;
      return;
    }

    this.saveBlacklist();
  }

  saveBlacklist() {
    const data: CampaignDetails = {
      ...this.campaign,
      engagementContactLists: [
        {
          ...this.campaign.engagementContactLists[0],
          contactBlackListed: this.blacklistedContacts,
        },
      ],
    };


    this.campaignsService
      .editCampaign(this.campaign.id, data, false)
      .subscribe(() => {
        this.onClose.emit();
        this.showBlacklistPrompt = false;
      });
  }

  // check if contact is blacklisted
  isContactBlacklisted(uuid: string): boolean {
    if (!this.blacklistedContacts) {
      return false;
    }
    const blacklistedArray = this.blacklistedContacts.split(',');
    return blacklistedArray.includes(uuid);
  }

  ngOnInit(): void {
    // console.log(this.displayedContacts, this.contacts);

    this.campaignsService.getCampaignDetails
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((campaign) => {
      this.campaign = campaign as CampaignDetails;
      this.blacklistedContacts =
        this.campaign.engagementContactLists[0]?.contactBlackListed;
    });

    this.contactsService.fetchGroupContacts(
      this.groupName,
      this.groupId,
      this.updatedParams
    );

    this.contactsService.getGroupContacts
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
      this.contacts = data;
      // this.updateDisplayedContacts();
    });

    this.contactsService.getGroupContactsPaginationData
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
      (data: IPagination) => {
        this.totalPages = data.totalPages;
        this.contactsPage = data.pageNumber;
      }
    );
  }

  ngOnDestroy(): void {
    // this.displayedContacts = [];
    this.debounceSearch.cancel();
    this.contacts = [];
  }
}
